// Tax rates and per-item assignments, creator-scoped.
//
// BACKED BY THE API since June 2026 (v2/tax/*) — localStorage is now only a
// cache so existing consumers keep their synchronous reads. Call
// hydrateTaxStore() on mount (TaxesContent / TaxPicker do); mutations write
// through to the backend, whose response (the whole store) replaces the
// cache — so FE-generated ids converge to server ids after the first save.
//
// Taxes are scoped to their creator: a self-employed stylist's taxes apply
// to their items, the owner's taxes to the salon's. Nothing in the industry
// does per-staff tax inside one account, which is the booth-rental gap this
// design serves.
//
// Both calculation modes are selectable. Inclusive (UK VAT) keeps tax
// inside the displayed price; exclusive (US sales tax) adds it on top at
// checkout. The mode only affects how sales totals are computed by the
// backend engine; displayed catalogue prices never change.

import { marketingBackend, isServerId } from '../marketing-v2/services/marketingBackend';
import { invalidateSellerTaxStore } from './sellerTax';

// Per location: calculation mode, the master switch and the VAT number are
// per site (the site's own row, else the creator's default), and so are
// site-only rates. One cache for every site showed site A's settings at B
// until the next hydrate.
const storageKey = (ownerId) => {
  let site = '';
  try { site = localStorage.getItem('salonId') || ''; } catch (e) { /* no storage */ }
  return `beu-taxes-${ownerId || 'default'}${site ? `-${site}` : ''}`;
};

// An empty store. No rate is invented: a seller with no rates is offered their
// country's preset on the Taxes page, applied only when they click it.
const seed = () => ({
  version: 1,
  calculationMode: 'inclusive',
  // Master switch (docs/tax-money-path.md §1 #10): rates do nothing until it is on.
  enabled: false,
  taxNumber: null,
  taxes: [],
  overrides: {},
});

export const loadTaxStore = (ownerId) => {
  try {
    const raw = localStorage.getItem(storageKey(ownerId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.taxes)) return parsed;
    }
  } catch (e) { /* fall through to seed */ }
  const fresh = seed(ownerId);
  try { localStorage.setItem(storageKey(ownerId), JSON.stringify(fresh)); } catch (e) { /* ignore */ }
  return fresh;
};

const persist = (ownerId, store) => {
  try { localStorage.setItem(storageKey(ownerId), JSON.stringify(store)); } catch (e) { /* ignore */ }
  return store;
};

const persistServer = (ownerId, serverStore) => persist(ownerId, {
  version: 1,
  calculationMode: serverStore.calculationMode || 'inclusive',
  enabled: serverStore.enabled === true,
  taxNumber: serverStore.taxNumber || null,
  taxes: serverStore.taxes || [],
  overrides: serverStore.overrides || {},
  otherSiteRateIds: serverStore.otherSiteRateIds || [],
});

/**
 * Pull the creator's store from the backend into the cache. Read-only: it
 * never writes a rate. (It used to push a default VAT rate for an empty store,
 * which ran on every mount of the Taxes page AND the item editors' tax picker,
 * created duplicates when two mounted at once, and hid the country presets.)
 */
export const hydrateTaxStore = async (ownerId) => {
  try {
    const server = await marketingBackend.taxList();
    return persistServer(ownerId, server);
  } catch (e) {
    console.error('tax hydrate failed (cache kept):', e.message);
    return loadTaxStore(ownerId);
  }
};

export const saveTax = (ownerId, tax) => {
  const store = loadTaxStore(ownerId);
  const now = new Date().toISOString();
  const stored = {
    appliesTo: { services: true, products: true, vouchers: false },
    enabled: true,
    ...tax,
    id: tax.id || `tax-${Date.now()}`,
    createdBy: ownerId,
    createdAt: tax.createdAt || now,
    updatedAt: now,
  };
  const exists = store.taxes.some((t) => t.id === stored.id);
  store.taxes = exists ? store.taxes.map((t) => (t.id === stored.id ? stored : t)) : [...store.taxes, stored];
  persist(ownerId, store);
  marketingBackend.taxSave({
    id: isServerId(stored.id) ? stored.id : undefined,
    name: stored.name, rate: stored.rate, appliesTo: stored.appliesTo, enabled: stored.enabled,
    scope: stored.scope === 'site' ? 'site' : 'all',
  }).then((server) => { invalidateSellerTaxStore(); return persistServer(ownerId, server); })
    .catch((e) => console.error('tax save failed:', e.message));
  return store.taxes;
};

export const setCalculationMode = (ownerId, mode) => {
  const store = loadTaxStore(ownerId);
  store.calculationMode = mode;
  persist(ownerId, store);
  marketingBackend.taxMode(mode)
    .then((server) => {
      invalidateSellerTaxStore();
      // The server answers with the whole store for this site.
      if (server && Array.isArray(server.taxes)) persistServer(ownerId, server);
    })
    .catch((e) => console.error('tax mode save failed:', e.message));
  return store.calculationMode;
};

/**
 * Master switch and VAT number. Optimistic in the cache, then the server;
 * the server is the truth the till reads, so a failed save is logged and the
 * next hydrate puts the cache back.
 */
export const setTaxSettings = (ownerId, patch, onError) => {
  const store = loadTaxStore(ownerId);
  if (patch.enabled !== undefined) store.enabled = patch.enabled === true;
  if (patch.taxNumber !== undefined) store.taxNumber = patch.taxNumber || null;
  persist(ownerId, store);
  marketingBackend.taxSettings(patch)
    .then((server) => { invalidateSellerTaxStore(); return persistServer(ownerId, server); })
    .catch((e) => {
      console.error('tax settings save failed:', e.message);
      // The server refused (e.g. switching tax on without a VAT number):
      // put the cache back so the till never reads a state that didn't save.
      if (patch.enabled !== undefined) { store.enabled = !store.enabled; persist(ownerId, store); }
      if (onError) onError(e);
    });
  return store;
};
export const deleteTax = (ownerId, taxId) => {
  const store = loadTaxStore(ownerId);
  store.taxes = store.taxes.filter((t) => t.id !== taxId);
  // Drop the deleted tax from any per-item overrides
  Object.keys(store.overrides).forEach((key) => {
    store.overrides[key] = { taxIds: (store.overrides[key].taxIds || []).filter((id) => id !== taxId) };
  });
  persist(ownerId, store);
  if (isServerId(taxId)) {
    marketingBackend.taxDelete(taxId)
      .then((server) => { invalidateSellerTaxStore(); return persistServer(ownerId, server); })
      .catch((e) => console.error('tax delete failed:', e.message));
  }
  return store.taxes;
};

// Per-item assignment. Absence of an override means "use the defaults for
// this domain"; an override with [] means "explicitly no tax on this item".
export const setItemTaxes = (ownerId, domain, itemId, taxIds) => {
  const store = loadTaxStore(ownerId);
  const key = `${domain}:${itemId}`;
  if (taxIds === null) {
    delete store.overrides[key];
  } else {
    store.overrides[key] = { taxIds };
  }
  persist(ownerId, store);
  // Only server tax ids mean anything to the backend; locals converge on hydrate.
  const serverIds = taxIds === null ? null : taxIds.filter(isServerId);
  marketingBackend.taxItemOverride(domain, String(itemId), serverIds)
    .then((server) => { invalidateSellerTaxStore(); return persistServer(ownerId, server); })
    .catch((e) => console.error('tax override save failed:', e.message));
};

export const getItemOverride = (ownerId, domain, itemId) => {
  const store = loadTaxStore(ownerId);
  const entry = store.overrides[`${domain}:${itemId}`];
  return entry ? entry.taxIds : null;
};

// The effective taxes for an item: its override if set, else every enabled
// tax whose defaults cover the domain.
export const getEffectiveTaxes = (ownerId, domain, itemId) => {
  const store = loadTaxStore(ownerId);
  const override = itemId ? store.overrides[`${domain}:${itemId}`] : null;
  if (override) {
    // Same rule as the server: an override naming only rates that live at
    // another location falls back to this site's defaults.
    const other = new Set((store.otherSiteRateIds || []).map(String));
    const ids = (override.taxIds || []).map(String);
    const here = ids.filter((id) => !other.has(id));
    if (!(ids.length > 0 && here.length === 0)) {
      return store.taxes.filter((t) => t.enabled && here.includes(String(t.id)));
    }
  }
  return store.taxes.filter((t) => t.enabled && t.appliesTo[domain]);
};

// Inclusive maths (the UK model): a 20% rate on £10.00 includes £1.67 tax.
// With multiple rates, each tax_i = price * r_i / (1 + sum(r)).
export const inclusiveTaxBreakdown = (pricePence, taxes) => {
  const totalRate = taxes.reduce((n, t) => n + (Number(t.rate) || 0), 0) / 100;
  // A 0% rate is still a line entry (zero-rated) — amount 0, shown by rate. Mirrors taxService.
  if (!totalRate) return taxes.map((t) => ({ id: t.id, name: t.name, rate: t.rate, amountPence: 0 }));
  return taxes.map((t) => ({
    id: t.id,
    name: t.name,
    rate: t.rate,
    amountPence: Math.round((pricePence * ((Number(t.rate) || 0) / 100)) / (1 + totalRate)),
  }));
};

// Exclusive maths (the US model): each tax is charged on top of the price,
// so a 20% rate on £10.00 adds £2.00 and the client pays £12.00.
export const exclusiveTaxBreakdown = (pricePence, taxes) => taxes
  .map((t) => ({
    id: t.id,
    name: t.name,
    rate: t.rate,
    amountPence: Math.round(pricePence * ((Number(t.rate) || 0) / 100)),   // 0% stays as a zero-rated entry
  }));

export const taxBreakdown = (pricePence, taxes, mode) => (mode === 'exclusive'
  ? exclusiveTaxBreakdown(pricePence, taxes)
  : inclusiveTaxBreakdown(pricePence, taxes));

export const currentOwnerId = () => localStorage.getItem('userId');
